import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UnitRepository } from '@app/domain/ports/tpv/unit.repository';
import { TpvUnitListItem, UnitCreatedResult } from '@app/domain/models/tpv/unit.model';
import { TpvUnitDetail } from '@app/domain/models/tpv/entity-detail.model';
import {
  CreateUnitRequest,
  CreateUnitResponseDto,
  UnitListItemDto,
} from '@app/data/dtos/tpv/unit.dto';
import { UnitDetailResponseDto } from '@app/data/dtos/tpv/entity-detail.dto';
import { UpdateUnitRequest } from '@app/data/dtos/tpv/company-update.dto';
import { mapUnitCreated, mapUnitListItem } from '@app/data/mappers/tpv/unit.mapper';
import { mapUnitDetail } from '@app/data/mappers/tpv/entity-detail.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/**
 * CRUD HTTP de unidades de negocio (multi-unidad, §2). Auth vía interceptores
 * estándar (Bearer + x-api-key); estos endpoints NO requieren `Idempotency-Key`.
 *
 * Rutas: `GET|POST /companies/<id_company>/units/` y
 * `PATCH|DELETE /companies/<id_company>/units/<id_unit>/`.
 */
@Injectable()
export class UnitRemoteDataSource extends UnitRepository {
  private readonly http = inject(HttpClient);

  override listUnits(idCompany: string): Observable<TpvUnitListItem[]> {
    return this.http
      .get<TpvEnvelope<UnitListItemDto[]> | UnitListItemDto[]>(
        tpvUrl(`companies/${idCompany}/units/`),
      )
      .pipe(
        map((resp) => {
          const data = unwrapTpv(resp);
          const rows = Array.isArray(data) ? data : [];
          return rows.map(mapUnitListItem);
        }),
      );
  }

  override getUnitById(idCompany: string, idUnit: string): Observable<TpvUnitDetail> {
    return this.http
      .get<TpvEnvelope<UnitDetailResponseDto> | UnitDetailResponseDto>(
        tpvUrl(`companies/${idCompany}/units/${idUnit}/`),
      )
      .pipe(map((resp) => mapUnitDetail(unwrapTpv<UnitDetailResponseDto>(resp))));
  }

  override createUnit(
    idCompany: string,
    payload: CreateUnitRequest,
  ): Observable<UnitCreatedResult> {
    return this.http
      .post<TpvEnvelope<CreateUnitResponseDto> | CreateUnitResponseDto>(
        tpvUrl(`companies/${idCompany}/units/`),
        payload,
      )
      .pipe(map((resp) => mapUnitCreated(unwrapTpv(resp))));
  }

  override updateUnit(
    idCompany: string,
    idUnit: string,
    payload: UpdateUnitRequest,
  ): Observable<void> {
    return this.http
      .patch<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/units/${idUnit}/`), payload)
      .pipe(map(() => void 0));
  }

  override deleteUnit(idCompany: string, idUnit: string): Observable<void> {
    return this.http
      .delete<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/units/${idUnit}/`))
      .pipe(map(() => void 0));
  }
}
